// @ts-check
// Configurações > Membros (doc 12, seção 11.3): convidar por e-mail com papel, convites pendentes (reenviar, copiar
// link, revogar) e a equipe com papel, desativação e remoção.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { PageCard, PageHeader } from '../page/page-card.js';
import { SettingsSection } from './settings-pages.js';
import { hasCurrentPermissionFlag } from '../../data/metadata-store.js';
import {
  listInvitations, listRoles, listTeamMembers, removeTeamMember, resendInvitation, revokeInvitation, sendInvitations, updateTeamMember,
} from '../../data/team.js';
import { workspaceMembersState } from '../../data/workspace-members.js';
import { formatFullName } from '../../lib/field-metadata.js';
import { inviteRoleOptions } from '../../lib/invite-roles.js';
import { Avatar } from '../../ui/avatar.js';
import { Button, IconButton } from '../../ui/button.js';
import { Tag } from '../../ui/chip.js';
import { Skeleton } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { Modal } from '../../ui/modal.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/team.js').TeamMember} TeamMember
 * @typedef {import('../../data/team.js').Invitation} Invitation
 * @typedef {import('../../data/team.js').Role} Role
 */

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** @param {string} text */
export async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // sem permissão da área de transferência: seleção manual
    const textarea = document.createElement('textarea');
    textarea.value = text;
    document.body.append(textarea);
    textarea.select();
    const isCopied = document.execCommand('copy');
    textarea.remove();
    return isCopied;
  }
}

/** @param {string} text */
export const parseEmailList = (text) => [...new Set(text.split(/[,;\s]+/).map((email) => email.trim().toLowerCase()).filter(Boolean))];

/** @param {string} isoDate */
const formatDate = (isoDate) => new Date(isoDate).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });

/**
 * Seletor de papel nativo (lista curta, acessível no celular). `defaultRoleLabel`: nome do papel padrão quando ele não
 * está entre as opções.
 * @param {{ roles: Role[], value: string, onChange: (roleId: string) => void, disabled?: boolean, ariaLabel: string, includeDefault?: boolean, defaultRoleId?: string,
 *   defaultRoleLabel?: string }} props
 */
export function RoleSelect({ roles, value, onChange, disabled = false, ariaLabel, includeDefault = false, defaultRoleId, defaultRoleLabel }) {
  const defaultRole = roles.find((role) => role.id === defaultRoleId);
  return html`<select class="text-input team-role-select" value=${value} disabled=${disabled} aria-label=${ariaLabel}
    onChange=${(event) => onChange(/** @type {HTMLSelectElement} */ (event.currentTarget).value)}>
    ${includeDefault ? html`<option value="">${t('team.members.defaultRoleOption', { role: defaultRole?.label ?? defaultRoleLabel ?? '' })}</option>` : null}
    ${roles.filter((role) => role.canBeAssignedToUsers).map((role) => html`<option key=${role.id} value=${role.id}>${role.label}</option>`)}
  </select>`;
}

/**
 * Só os papéis que o servidor aceita para quem convida; sem nenhum, uma nota no lugar do formulário.
 * @param {{ roles: Role[], defaultRoleId: string, invitableRoleIds?: string[], onInvited: (invitations: Invitation[]) => void }} props
 */
function InviteForm({ roles, defaultRoleId, invitableRoleIds, onInvited }) {
  const options = inviteRoleOptions(roles, defaultRoleId, invitableRoleIds);
  const [emailsText, setEmailsText] = useState('');
  const [roleId, setRoleId] = useState(options.initialRoleId);
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const [isSending, setIsSending] = useState(false);

  const submit = async (/** @type {Event} */ event) => {
    event.preventDefault();
    const emails = parseEmailList(emailsText);
    if (emails.length === 0) {
      setError(t('team.invite.form.emptyEmails'));
      return;
    }
    const invalid = emails.filter((email) => !EMAIL_PATTERN.test(email));
    if (invalid.length > 0) {
      setError(t('team.invite.form.invalidEmails', { emails: invalid.join(', ') }));
      return;
    }
    setError(null);
    setIsSending(true);
    try {
      const result = await sendInvitations({ emails, roleId: roleId || null });
      if (result.invitations.length > 0) {
        showSuccessSnackbar(t('team.invite.form.sent', { count: result.invitations.length }));
        setEmailsText('');
      }
      if (result.errors.length > 0) {
        setError(result.errors.map((message) => {
          const [email, ...rest] = message.split(' ');
          const reason = rest.join(' ');
          return reason === 'already invited' ? t('team.invite.form.alreadyInvited', { email }) : t('team.invite.form.alreadyMember', { email });
        }).join(' '));
      }
      onInvited(result.invitations);
    } catch (sendError) {
      showErrorSnackbar(sendError);
    } finally {
      setIsSending(false);
    }
  };

  if (!options.canInvite) {
    return html`<p class="team-empty team-invite-unavailable" role="note"><${Icon} name="IconInfoCircle" size=${14} /> ${t('team.invite.form.noInvitableRoles')}</p>`;
  }
  return html`<form class="team-invite-form" onSubmit=${submit}>
    <label class="form-field team-invite-emails">
      <span class="form-field-label">${t('team.invite.form.emailsLabel')}</span>
      <input class="text-input" type="text" value=${emailsText} placeholder=${t('team.invite.form.emailsPlaceholder')} autocomplete="off"
        onInput=${(event) => {
          setEmailsText(/** @type {HTMLInputElement} */ (event.currentTarget).value);
          if (error) setError(null);
        }} />
    </label>
    <label class="form-field team-invite-role">
      <span class="form-field-label">${t('team.invite.form.roleLabel')}</span>
      <${RoleSelect} roles=${options.roles} value=${roleId} onChange=${setRoleId} ariaLabel=${t('team.invite.form.roleLabel')}
        includeDefault=${options.includeDefault} defaultRoleId=${defaultRoleId} defaultRoleLabel=${roles.find((role) => role.id === defaultRoleId)?.label} />
    </label>
    <div class="team-invite-submit">
      <${Button} title=${t('team.invite.form.submit')} icon="IconSend" accent="blue" size="small" type="submit" isLoading=${isSending} />
    </div>
    ${error ? html`<div class="form-error team-invite-error" role="alert"><${Icon} name="IconAlertTriangle" size=${14} /><span>${error}</span></div>` : null}
  </form>`;
}

/**
 * @param {{ invitations: Invitation[], roles: Role[], inviteUrls: Record<string, string>, onChanged: (invitation?: Invitation) => void }} props
 */
function PendingInvitations({ invitations, roles, inviteUrls, onChanged }) {
  const [busyId, setBusyId] = useState(/** @type {string | null} */ (null));
  if (invitations.length === 0) return html`<p class="team-empty">${t('team.invitations.empty')}</p>`;
  const roleLabel = (/** @type {string | null} */ roleId) => roles.find((role) => role.id === roleId)?.label ?? '';

  /** @param {Invitation} invitation */
  const resend = async (invitation) => {
    setBusyId(invitation.id);
    try {
      const renewed = await resendInvitation(invitation.id);
      showSuccessSnackbar(renewed?.emailSent === false ? t('team.invitations.resentWithoutEmail') : t('team.invitations.resent', { email: invitation.email }));
      onChanged(renewed);
    } catch (resendError) {
      showErrorSnackbar(resendError);
    } finally {
      setBusyId(null);
    }
  };

  /** @param {Invitation} invitation */
  const revoke = async (invitation) => {
    setBusyId(invitation.id);
    try {
      await revokeInvitation(invitation.id);
      showSuccessSnackbar(t('team.invitations.revoked', { email: invitation.email }));
      onChanged();
    } catch (revokeError) {
      showErrorSnackbar(revokeError);
    } finally {
      setBusyId(null);
    }
  };

  return html`<div class="settings-table team-table">
    <div class="settings-table-row settings-table-header team-row" data-columns="invitations">
      <span>${t('team.invitations.columns.email')}</span>
      <span>${t('team.invitations.columns.role')}</span>
      <span>${t('team.invitations.columns.expires')}</span>
      <span></span>
    </div>
    ${invitations.map((invitation) => {
      const inviteUrl = inviteUrls[invitation.id];
      return html`<div key=${invitation.id} class="settings-table-row team-row" data-columns="invitations">
        <span class="settings-table-name"><${Icon} name="IconMail" size=${16} />${invitation.email}</span>
        <span class="settings-table-muted">${roleLabel(invitation.roleId)}</span>
        <span class="settings-table-muted">
          ${invitation.status === 'EXPIRED'
            ? html`<${Tag} color="red" text=${t('team.invitations.expired')} />`
            : t('team.invitations.expiresAt', { date: formatDate(invitation.expiresAt) })}
        </span>
        <span class="team-row-actions">
          ${inviteUrl
            ? html`<${IconButton} icon="IconLink" size="small" variant="tertiary" ariaLabel=${t('team.invitations.copyLink')}
                onClick=${async () => {
                  if (await copyText(inviteUrl)) showSuccessSnackbar(t('team.invitations.linkCopied'));
                }} />`
            : null}
          <${IconButton} icon="IconRefresh" size="small" variant="tertiary" ariaLabel=${t('team.invitations.resendTo', { email: invitation.email })} disabled=${busyId === invitation.id} onClick=${() => resend(invitation)} />
          <${IconButton} icon="IconX" size="small" variant="tertiary" accent="danger" ariaLabel=${t('team.invitations.revokeFor', { email: invitation.email })} disabled=${busyId === invitation.id} onClick=${() => revoke(invitation)} />
        </span>
      </div>`;
    })}
  </div>`;
}

/**
 * @param {{ members: TeamMember[], roles: Role[], canManageMembers: boolean, canManageRoles: boolean, onChanged: () => void }} props
 */
function MemberList({ members, roles, canManageMembers, canManageRoles, onChanged }) {
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState(/** @type {string | null} */ (null));
  const [memberToRemove, setMemberToRemove] = useState(/** @type {TeamMember | null} */ (null));
  const [isRemoving, setIsRemoving] = useState(false);
  const query = search.trim().toLowerCase();
  const visible = members.filter((member) => !query || `${formatFullName(member.name ?? undefined)} ${member.userEmail ?? ''}`.toLowerCase().includes(query));

  /** @param {TeamMember} member @param {{ roleId?: string, isActive?: boolean }} patch @param {string} successMessage */
  const update = async (member, patch, successMessage) => {
    setBusyId(member.id);
    try {
      await updateTeamMember(member.id, patch);
      showSuccessSnackbar(successMessage);
      onChanged();
    } catch (updateError) {
      showErrorSnackbar(updateError);
    } finally {
      setBusyId(null);
    }
  };

  const confirmRemove = async () => {
    if (!memberToRemove) return;
    setIsRemoving(true);
    try {
      await removeTeamMember(memberToRemove.id);
      showSuccessSnackbar(t('team.members.removed'));
      // os seletores de responsável voltam a ler a lista sem o removido
      workspaceMembersState.value = null;
      setMemberToRemove(null);
      onChanged();
    } catch (removeError) {
      showErrorSnackbar(removeError);
    } finally {
      setIsRemoving(false);
    }
  };

  return html`
    <input class="text-input team-search" type="search" value=${search} placeholder=${t('team.members.searchPlaceholder')} aria-label=${t('team.members.searchPlaceholder')}
      onInput=${(event) => setSearch(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
    <div class="settings-table team-table">
      <div class="settings-table-row settings-table-header team-row" data-columns="members">
        <span>${t('team.members.columns.name')}</span>
        <span>${t('team.members.columns.role')}</span>
        <span>${t('team.members.columns.status')}</span>
        <span></span>
      </div>
      ${visible.map((member) => {
        const fullName = formatFullName(member.name ?? undefined) || member.userEmail || t('record.untitled');
        const isLocked = Boolean(member.isCurrentUser) || busyId === member.id;
        return html`<div key=${member.id} class="settings-table-row team-row" data-columns="members" data-inactive=${member.isActive ? undefined : 'true'}>
          <span class="team-member">
            <${Avatar} placeholder=${fullName} seed=${member.id} size="md" type="rounded" avatarUrl=${member.avatarUrl ?? undefined} />
            <span class="team-member-texts">
              <span class="team-member-name">${fullName}${member.isCurrentUser ? html` <span class="team-member-you">${t('team.members.you')}</span>` : null}</span>
              <span class="team-member-email">${member.userEmail ?? ''}</span>
            </span>
          </span>
          <span>
            ${canManageRoles && !member.isCurrentUser
              ? html`<${RoleSelect} roles=${roles} value=${member.roleId} disabled=${isLocked} ariaLabel=${t('team.members.roleFor', { name: fullName })}
                  onChange=${(roleId) => update(member, { roleId }, t('team.members.roleChanged', { name: fullName }))} />`
              : html`<span class="settings-table-muted">${roles.find((role) => role.id === member.roleId)?.label ?? ''}</span>`}
          </span>
          <span>
            ${member.isActive
              ? html`<${Tag} color="green" text=${t('team.members.active')} />`
              : html`<${Tag} color="gray" text=${t('team.members.inactive')} />`}
          </span>
          <span class="team-row-actions">
            ${canManageMembers && !member.isCurrentUser
              ? html`
                  <${IconButton} icon=${member.isActive ? 'IconUserX' : 'IconUserCheck'} size="small" variant="tertiary" disabled=${isLocked}
                    ariaLabel=${member.isActive ? t('team.members.deactivateFor', { name: fullName }) : t('team.members.reactivateFor', { name: fullName })}
                    onClick=${() => update(member, { isActive: !member.isActive }, member.isActive ? t('team.members.deactivated', { name: fullName }) : t('team.members.reactivated', { name: fullName }))} />
                  <${IconButton} icon="IconTrash" size="small" variant="tertiary" accent="danger" disabled=${isLocked} ariaLabel=${t('team.members.removeFor', { name: fullName })} onClick=${() => setMemberToRemove(member)} />`
              : null}
          </span>
        </div>`;
      })}
    </div>
    ${memberToRemove
      ? html`<${Modal}
          title=${t('team.members.removeTitle')}
          subtitle=${t('team.members.removeWarning', { name: formatFullName(memberToRemove.name ?? undefined) || memberToRemove.userEmail || '' })}
          confirmLabel=${t('team.members.remove')}
          confirmAccent="danger"
          isLoading=${isRemoving}
          onClose=${() => setMemberToRemove(null)}
          onConfirm=${confirmRemove}
        />`
      : null}
  `;
}

export function SettingsMembersPage() {
  const canManageMembers = hasCurrentPermissionFlag('WORKSPACE_MEMBERS');
  const canManageRoles = hasCurrentPermissionFlag('ROLES');
  const [members, setMembers] = useState(/** @type {TeamMember[] | null} */ (null));
  const [invitations, setInvitations] = useState(/** @type {Invitation[]} */ ([]));
  const [rolesData, setRolesData] = useState(/** @type {{ roles: Role[], defaultRoleId: string, invitableRoleIds?: string[] } | null} */ (null));
  // links dos convites criados ou reenviados nesta tela (o servidor não guarda o token para mostrar depois)
  const [inviteUrls, setInviteUrls] = useState(/** @type {Record<string, string>} */ ({}));

  const loadMembers = () => listTeamMembers().then(setMembers).catch((error) => {
    showErrorSnackbar(error);
    setMembers([]);
  });
  const loadInvitations = () => (canManageMembers ? listInvitations().then(setInvitations).catch(showErrorSnackbar) : Promise.resolve());

  useEffect(() => {
    document.title = `${t('team.members.title')} - ${t('navigation.settings')}`;
    loadMembers();
    loadInvitations();
    listRoles().then(setRolesData).catch(showErrorSnackbar);
  }, []);

  /** @param {Invitation[]} created */
  const rememberInviteUrls = (created) => {
    const urls = Object.fromEntries(created.filter((invitation) => invitation.inviteUrl).map((invitation) => [invitation.id, /** @type {string} */ (invitation.inviteUrl)]));
    if (Object.keys(urls).length > 0) setInviteUrls((current) => ({ ...current, ...urls }));
  };

  const roles = rolesData?.roles ?? [];
  return html`<${PageCard} header=${html`<${PageHeader} icon="IconUsers" title=${t('team.members.title')} />`}>
    <div class="settings-page">
      ${canManageMembers
        ? html`<${SettingsSection} title=${t('team.invite.form.title')} description=${t('team.invite.form.description')}>
            ${rolesData
              ? html`<${InviteForm} roles=${roles} defaultRoleId=${rolesData.defaultRoleId} invitableRoleIds=${rolesData.invitableRoleIds}
                  onInvited=${(/** @type {Invitation[]} */ created) => {
                    rememberInviteUrls(created);
                    loadInvitations();
                  }} />`
              : html`<${Skeleton} height=${64} radius=${8} />`}
          <//>
          <${SettingsSection} title=${t('team.invitations.title')} description=${t('team.invitations.description')}>
            <${PendingInvitations} invitations=${invitations} roles=${roles} inviteUrls=${inviteUrls}
              onChanged=${(/** @type {Invitation | undefined} */ renewed) => {
                if (renewed) rememberInviteUrls([renewed]);
                loadInvitations();
              }} />
          <//>`
        : null}
      <${SettingsSection} title=${t('team.members.listTitle')} description=${t('team.members.description')}>
        ${members && rolesData
          ? html`<${MemberList} members=${members} roles=${roles} canManageMembers=${canManageMembers} canManageRoles=${canManageRoles} onChanged=${loadMembers} />`
          : html`<${Skeleton} height=${48} radius=${8} /><${Skeleton} height=${48} radius=${8} />`}
      <//>
    </div>
  <//>`;
}
